import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { api, ApiError, legacyAppUrl } from '@/lib/api'
import { canFetchRhKpis } from '@/lib/rbac'
import { Alert, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'

type RhKpis = {
  total: number
  em_ferias: number
  afastados: number
  adm_mes: number
  deslig_mes: number
  turnover: number
  aval_pend: number
}

async function fetchRhKpis() {
  const res = await api.get<{ sucesso?: boolean; dados?: RhKpis }>('/rh/colaboradores/kpis/')
  if (!res.dados) throw new ApiError('Resposta de KPIs inválida.', 500, res)
  return res.dados
}

export function HomePage() {
  const { user, perfil, perfilLabel, perfilCor, canView, isColab } = useAuth()
  const nome = user?.nome || user?.name || 'time'
  const showKpis = canFetchRhKpis(perfil)

  const kpis = useQuery({
    queryKey: ['rh', 'colaboradores', 'kpis'],
    queryFn: fetchRhKpis,
    enabled: showKpis,
  })

  if (isColab) {
    return (
      <section className="hi-page">
        <PageHeader
          title={`Olá, ${nome}`}
          subtitle="Seu acesso está no portal do colaborador. Demais módulos abrem no painel legado conforme o perfil."
          actions={
            <>
              <Link className="btn btn-primary" to="/app/colaborador">
                Abrir meu painel
              </Link>
              <a className="btn btn-secondary" href={legacyAppUrl('#/portal')}>
                Portal legado
              </a>
            </>
          }
        />
      </section>
    )
  }

  return (
    <section className="hi-page">
      <PageHeader
        title={`Olá, ${nome}`}
        subtitle={
          <>
            Visão geral do sistema. Perfil{' '}
            <span className="hi-perfil-pill" style={{ color: perfilCor, borderColor: `${perfilCor}55` }}>
              {perfilLabel}
            </span>
            .
          </>
        }
        actions={
          <>
            {canView('recrutamento') ? (
              <Link className="btn btn-primary" to="/app/recrutamento">
                Recrutamento
              </Link>
            ) : null}
            {canView('colaboradores') ? (
              <Link className="btn btn-secondary" to="/app/rh">
                Colaboradores
              </Link>
            ) : null}
            <Link className="btn btn-secondary" to="/carreiras">
              Carreiras públicas
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/')}>
              Painel legado
            </a>
          </>
        }
      />

      {showKpis ? (
        <div aria-live="polite" className="hi-stack">
          {kpis.isLoading ? <Muted>Carregando indicadores…</Muted> : null}
          {kpis.isError ? (
            <Alert tone="danger">
              {kpis.error instanceof ApiError ? kpis.error.message : 'Falha ao carregar KPIs.'}
            </Alert>
          ) : null}
          {kpis.data ? (
            <KpiGrid>
              <Kpi label="Colaboradores ativos" value={kpis.data.total.toLocaleString('pt-BR')} />
              <Kpi label="Admissões no mês" value={kpis.data.adm_mes.toLocaleString('pt-BR')} />
              <Kpi label="Desligamentos no mês" value={kpis.data.deslig_mes.toLocaleString('pt-BR')} />
              <Kpi label="Em férias" value={kpis.data.em_ferias.toLocaleString('pt-BR')} />
              <Kpi label="Afastados" value={kpis.data.afastados.toLocaleString('pt-BR')} />
              <Kpi label="Turnover %" value={kpis.data.turnover.toLocaleString('pt-BR')} />
              <Kpi label="Avaliações pendentes" value={kpis.data.aval_pend.toLocaleString('pt-BR')} />
            </KpiGrid>
          ) : null}
        </div>
      ) : (
        <Section>
          <Muted>Seu perfil não inclui KPIs de headcount neste painel.</Muted>
        </Section>
      )}
    </section>
  )
}

export default HomePage
